import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { usePeople } from '@/api/queries/people-queries'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import type { CommitmentException, CommitmentExceptionReason } from '@/types'

const REASON_LABELS: Record<CommitmentExceptionReason, string> = {
	FERIAS: 'Férias',
	ATESTADO: 'Atestado',
	AFASTAMENTO: 'Afastamento',
	TRANSFERIDO: 'Transferido',
	FOLGA_DE_CAMPO: 'Folga de campo',
	EM_MOBILIZACAO: 'Em mobilização',
	LICENCA_MATERNIDADE: 'Licença maternidade',
	OUTRO: 'Outro',
}

export interface CommitmentExceptionFormValues {
	personId: string
	startDate: string
	endDate: string
	reason: CommitmentExceptionReason
}

interface CommitmentExceptionFormProps {
	initial?: CommitmentException
	onSave: (values: CommitmentExceptionFormValues) => void
	onClose: () => void
	isSaving?: boolean
	/**
	 * Trava o pedido a essa pessoa e esconde o seletor de colaborador —
	 * usado na tela self-service, onde o usuário só pode pedir para si.
	 */
	lockedPersonId?: string
}

export function CommitmentExceptionFormContent({
	initial,
	onSave,
	onClose,
	isSaving = false,
	lockedPersonId,
}: CommitmentExceptionFormProps) {
	const [personId, setPersonId] = useState(
		lockedPersonId ?? initial?.personId ?? '',
	)
	const [startDate, setStartDate] = useState(initial?.startDate ?? '')
	const [endDate, setEndDate] = useState(initial?.endDate ?? '')
	const [reason, setReason] = useState<CommitmentExceptionReason | ''>(
		initial?.reason ?? '',
	)

	const { data: people, isLoading: isLoadingPeople } = usePeople(true)

	const personOptions = useMemo(
		() =>
			(people ?? []).map((person) => ({
				value: person.id,
				label: person.name,
			})),
		[people],
	)

	const handleSave = () => {
		if (!personId) {
			toast.error(
				lockedPersonId
					? 'Não foi possível identificar seu cadastro de colaborador.'
					: 'Selecione o colaborador.',
			)
			return
		}
		if (!startDate || !endDate) {
			toast.error('Informe o período (data início e fim).')
			return
		}
		if (new Date(endDate) < new Date(startDate)) {
			toast.error('A data final não pode ser anterior à data inicial.')
			return
		}
		if (!reason) {
			toast.error('Selecione o motivo.')
			return
		}

		onSave({ personId, startDate, endDate, reason })
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			{!lockedPersonId && (
				<Field>
					<FieldLabel htmlFor="exception-person">Colaborador</FieldLabel>
					<FieldContent>
						<SearchableSelect
							id="exception-person"
							options={personOptions}
							value={personId}
							onValueChange={setPersonId}
							placeholder={
								isLoadingPeople
									? 'Carregando colaboradores...'
									: 'Selecione um colaborador'
							}
							searchPlaceholder="Buscar colaborador..."
							emptyMessage="Nenhum colaborador encontrado"
							disabled={isLoadingPeople}
						/>
					</FieldContent>
				</Field>
			)}

			<div className="grid grid-cols-2 gap-3">
				<Field>
					<FieldLabel htmlFor="exception-start">Início</FieldLabel>
					<FieldContent>
						<Input
							id="exception-start"
							type="date"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
						/>
					</FieldContent>
				</Field>
				<Field>
					<FieldLabel htmlFor="exception-end">Fim</FieldLabel>
					<FieldContent>
						<Input
							id="exception-end"
							type="date"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
						/>
					</FieldContent>
				</Field>
			</div>

			<Field>
				<FieldLabel htmlFor="exception-reason">Motivo</FieldLabel>
				<FieldContent>
					<NativeSelect
						id="exception-reason"
						value={reason}
						onChange={(e) =>
							setReason(e.target.value as CommitmentExceptionReason)
						}
					>
						<NativeSelectOption value="" disabled>
							Selecione o motivo
						</NativeSelectOption>
						{(Object.keys(REASON_LABELS) as CommitmentExceptionReason[]).map(
							(key) => (
								<NativeSelectOption key={key} value={key}>
									{REASON_LABELS[key]}
								</NativeSelectOption>
							),
						)}
					</NativeSelect>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
					disabled={isSaving}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={isSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{isSaving ? 'Registrando...' : 'Registrar exceção'}
				</Button>
			</div>
		</div>
	)
}

export { REASON_LABELS }
